import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, UserX, Scale, Coins, Gavel, FileText, FileSignature, HelpCircle, ShieldCheck, ArrowRight, ListChecks, Search, GitCompare, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/24a9988c-9f50-4232-8b87-cdd4daf87c63/images/e0b3a0d3c13982bf0874ffd9834b737535bc03c45370cc23c3f715426b2ad45e.png";

const PROBLEMS = [
  { key: "faulty_product", q: "Is a product faulty, unsafe or not as described?", icon: Briefcase },
  { key: "poor_service", q: "Was a service poor, incomplete or not provided?", icon: UserX },
  { key: "refund", q: "Has a business refused or delayed your refund?", icon: Coins },
  { key: "cancellation", q: "Do you need to cancel an order, booking or contract?", icon: Gavel },
  { key: "subscription", q: "Are you stuck in a subscription or recurring payment?", icon: FileSignature },
  { key: "travel", q: "Do you have a travel, ticket or accommodation dispute?", icon: FileText },
  { key: "chargeback", q: "Do you want to pursue chargeback or Section 75?", icon: Scale },
  { key: "unsure", q: "Not sure what type of help you need?", icon: HelpCircle },
];

const FEATURES = [
  { icon: ListChecks, t: "Guided intake", d: "A calm, one-question-at-a-time interview that gathers the facts and builds a chronology." },
  { icon: Search, t: "Rights & route finder", d: "Surfaces possible remedies, payment protections, escalation routes and sources to verify." },
  { icon: GitCompare, t: "Evidence & document review", d: "Compares contracts, terms, receipts, complaint replies and supporting evidence." },
];

const shadow = { textShadow: "0 2px 18px rgba(15,26,19,0.55)" };

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`reveal ${vis ? "is-visible" : ""} ${className}`}>{children}</div>;
}

export default function Landing() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);
  const [forms, setForms] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [y, setY] = useState(0);

  useEffect(() => {
    api.get("/gov-forms").then(({ data }) => setForms(data)).catch(() => {});
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { setY(window.scrollY); setScrolled(window.scrollY > 40); raf = null; });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const start = async (key) => {
    setLoading(key);
    try {
      const { data } = await api.post("/matters", { problem_type: key, client_name: "New client" });
      nav(`/intake/${data.id}`);
    } catch { toast.error("Could not start. Please try again."); setLoading(null); }
  };

  const startBusiness = async () => {
    setLoading("business");
    try {
      const { data } = await api.post("/matters", { problem_type: "business_response", party: "respondent", client_name: "Business" });
      nav(`/intake/${data.id}`);
    } catch { toast.error("Could not start. Please try again."); setLoading(null); }
  };

  const glass = "rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md";

  return (
    <div className="relative min-h-screen text-white">
      {/* Fixed, crisp full-page mountain background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(15,26,19,0.45), rgba(15,26,19,0.35) 45%, rgba(15,26,19,0.5))" }} />
      </div>

      {/* Nav */}
      <header className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${scrolled ? "border-b border-white/15 backdrop-blur-xl" : ""}`} style={scrolled ? { background: "rgba(15,26,19,0.55)" } : {}}>
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 text-white">
          <span className="font-serif text-2xl font-medium tracking-tight" style={shadow}>AI Consumer Champion</span>
          <nav className="flex items-center gap-1.5">
            <button data-testid="nav-forms-link" onClick={() => nav("/forms")} className="hidden rounded-full px-4 py-2 text-sm transition-colors hover:bg-white/15 sm:block">Consumer guidance</button>
            <button data-testid="open-dashboard-btn" onClick={() => nav("/app")} className="rounded-full border border-white/40 px-5 py-2 text-sm font-medium transition-colors hover:bg-white/15">Dashboard</button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex min-h-screen items-center">
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-28" style={{ transform: `translateY(${y * -0.1}px)`, opacity: Math.max(0.15, 1 - y / 900) }}>
          <div className="max-w-3xl animate-fade-up">
            <div className={`mb-6 inline-flex items-center gap-2 ${glass} px-3.5 py-1.5 text-xs font-medium`}>
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-200" /> Evidence-led claims support — you stay in control
            </div>
            <h1 className="font-serif text-5xl font-light leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl" style={shadow}>
              Consumer claims,<br /><span className="italic text-emerald-100">championed by AI.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/90" style={shadow}>
              Turn a frustrating purchase into a clear action plan: organise evidence, draft complaints,
              request refunds, cancel contracts and prepare escalation routes in one calm workflow.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button data-testid="hero-start-btn" size="lg" className="rounded-full bg-white px-8 text-[#1a2e21] hover:bg-white/90" onClick={() => document.getElementById("problems").scrollIntoView({ behavior: "smooth" })}>
                Start your claim <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button data-testid="hero-dashboard-btn" size="lg" variant="ghost" className="rounded-full border border-white/40 px-8 text-white hover:bg-white/15 hover:text-white" onClick={() => nav("/app")}>
                Open dashboard
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-xs uppercase tracking-[0.3em] text-white/70">Scroll</div>
      </section>

      {/* Problem selector */}
      <section id="problems" className="relative border-t border-white/10 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="mb-2 text-xs font-medium uppercase tracking-[0.25em] text-emerald-100/90" style={shadow}>Get started</div>
            <h2 className="font-serif text-4xl font-light tracking-tight sm:text-5xl" style={shadow}>
              What went wrong<br className="hidden sm:block" /> with your purchase?
            </h2>
            <p className="mt-4 max-w-xl text-white/85" style={shadow}>Choose what best describes your situation. We’ll organise the facts, evidence, remedy request and next escalation step.</p>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PROBLEMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.key} delay={i * 70}>
                  <button data-testid={`problem-card-${p.key}`} disabled={loading} onClick={() => start(p.key)}
                    className={`group flex h-full w-full flex-col items-start ${glass} p-6 text-left transition-colors duration-200 hover:-translate-y-1.5 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 disabled:opacity-50`}
                    style={{ transitionProperty: "transform, background-color, border-color" }}>
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-emerald-100 transition-colors group-hover:bg-white group-hover:text-[#1a2e21]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="mt-5 text-base font-medium leading-snug">{p.q}</span>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-100">
                      {loading === p.key ? "Starting…" : "Begin"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Business intake */}
      <section className="relative border-t border-white/10 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className={`overflow-hidden ${glass} p-10 sm:p-14`}>
              <div className="grid items-center gap-8 lg:grid-cols-2">
                <div>
                  <div className="mb-2 text-xs font-medium uppercase tracking-[0.25em] text-emerald-100/90">For businesses</div>
                  <h2 className="font-serif text-4xl font-light tracking-tight sm:text-5xl">Responding to a complaint?</h2>
                  <p className="mt-4 max-w-lg text-white/85">
                    If your organisation has received a consumer complaint, gather the transaction facts,
                    compare the requested remedy with your records, and prepare a clear, fair response.
                  </p>
                  <div className="mt-8">
                    <Button data-testid="business-intake-btn" size="lg" disabled={loading} className="rounded-full bg-white px-8 text-[#1a2e21] hover:bg-white/90" onClick={startBusiness}>
                      {loading === "business" ? "Starting…" : "Start a business intake"} <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid gap-3">
                  {[
                    { t: "Confirm business details", d: "Organisation name, address and contact." },
                    { t: "Review the evidence", d: "Compare the complaint with receipts, terms and correspondence." },
                    { t: "Draft a fair response", d: "AI-assisted wording based only on the supplied facts." },
                  ].map((x, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-2xl border border-white/15 bg-white/10 p-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15 text-emerald-100"><Scale className="h-4 w-4" /></span>
                      <div><p className="text-sm font-medium">{x.t}</p><p className="text-xs text-white/70">{x.d}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Consumer guidance */}
      <section className="relative border-t border-white/10 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-[0.25em] text-emerald-100/90" style={shadow}>Official sources</div>
                <h2 className="font-serif text-4xl font-light tracking-tight sm:text-5xl" style={shadow}>Consumer rights guidance</h2>
                <p className="mt-4 max-w-xl text-white/85" style={shadow}>Official UK guidance on consumer rights, returns, refunds, cancellations and fair contract terms.</p>
              </div>
              <Button data-testid="all-forms-btn" variant="ghost" className="rounded-full border border-white/40 text-white hover:bg-white/15 hover:text-white" onClick={() => nav("/forms")}>
                View all guidance <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(forms?.forms || []).slice(0, 6).map((f, i) => (
              <Reveal key={f.ref} delay={i * 70}>
                <a href={f.url} target="_blank" rel="noopener noreferrer" data-testid={`home-form-${f.ref}`}
                  className={`group flex h-full items-start gap-3 ${glass} p-5 transition-colors duration-200 hover:-translate-y-1.5 hover:bg-white/20`}
                  style={{ borderColor: f.primary ? "rgba(190,242,200,0.5)" : undefined, transitionProperty: "transform, background-color, border-color" }}>
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${f.primary ? "bg-white text-[#1a2e21]" : "bg-white/15 text-emerald-100"}`}><FileText className="h-5 w-5" /></span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-white/15 px-1.5 py-0.5 font-mono text-xs font-medium text-white/90">{f.ref}</span>
                      {f.primary && <span className="rounded bg-emerald-300/25 px-1.5 py-0.5 text-xs font-medium text-emerald-100">MVP focus</span>}
                    </div>
                    <h3 className="mt-1.5 text-sm font-medium leading-snug">{f.title}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-100">Open on GOV.UK <ExternalLink className="h-3 w-3" /></span>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative border-t border-white/10 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10 md:grid-cols-3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.t} delay={i * 90}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-emerald-100"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-serif text-2xl font-light" style={shadow}>{f.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/80" style={shadow}>{f.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="relative border-t border-white/10 py-12">
        <div className="mx-auto max-w-7xl px-6 text-sm text-white/70">
          <p className="max-w-3xl" style={shadow}>AI Consumer Champion provides information, evidence organisation and drafting support—not legal advice or a guaranteed result. Check deadlines, payment-scheme rules and legal authorities with the relevant provider or an independent adviser.</p>
        </div>
      </footer>
    </div>
  );
}
